import {
  Avatar,
  AvatarGroup,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import DragHandleOutlinedIcon from "@mui/icons-material/DragHandleOutlined";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, Stack } from "@mui/system";
import GroupedOccurrencesTable from "./GroupedOccurrencesTable";
// import CommentsAndFiles from "../../../../Discussion/CommentsAndFiles";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  filterAndGroupTasks,
  mapOwnersToNames,
} from "../DataAccuracyHelpers/helpers";
import { formatDate } from "../../../../../utils/helpers";
import {
  selectAllUsers,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { useEffect, useMemo, useState } from "react";
import SqlEditor from "../../../../Admin/DatastoreRequests/SqlEditor/SqlEditor";

const TaskNameDataAccuracy = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const {
    tasksByTaskStructureSharedUid = [],
    dataAccruracyData,
    bizLayerQuery,
  } = useSelector((state: any) => state.dataAccuracy || {});
  const [isQuerySourceOpen, setIsQuerySourceOpen] = useState(false);
  const taskStructureSharedUid = searchParams.get("task_structure_shared_uid");

  const filterdTaskDetails = filterAndGroupTasks(
    dataAccruracyData?.query_output,
    taskStructureSharedUid,
  );

  const lastUpdatedFromTasks = useMemo(() => {
    if (!tasksByTaskStructureSharedUid?.length) return "";

    return tasksByTaskStructureSharedUid
      .map((task: any) => task.modified_at)
      .filter(Boolean)
      .sort(
        (a: string, b: string) => new Date(b).getTime() - new Date(a).getTime(),
      )[0];
  }, [tasksByTaskStructureSharedUid]);

  const firstTask = filterdTaskDetails?.[0] || {};

  const {
    title = "",
    websites = [],
    category = "",
    owners = [],
    // last_updated = "",
    priority = "",
    description = "",
  } = firstTask;

  const allUsers = useSelector(selectAllUsers);
  const ownerNames = mapOwnersToNames(owners, allUsers);

  useEffect(() => {
    if (title) {
      const newParams = new URLSearchParams(searchParams);
      if (newParams.get("taskTitle") !== title) {
        newParams.set("taskTitle", title);
        setSearchParams(newParams, { replace: true });
      }
    }
    // eslint-disable-next-line
  }, [title]); // Only depend on title, not searchParams

  const handleCopyQuery = async () => {
    try {
      await navigator.clipboard.writeText(bizLayerQuery || "");
      dispatch(
        setDisplayErrorSnackBar({
          message: "Query copied to clipboard",
          type: "success",
        }),
      );
    } catch (e) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to copy query",
          type: "error",
        }),
      );
    }
  };

  return (
    <>
      <Paper sx={{ padding: "8px 16px" }}>
        <Stack direction="row" gap={1} alignItems="center">
          <Typography variant="h6" component="h2" gutterBottom>
            {title}
          </Typography>
          <>
            {priority?.toLowerCase() === "high" ? (
              <KeyboardArrowUpIcon sx={{ color: "#D32F2F" }} />
            ) : priority?.toLowerCase() === "low" ? (
              <KeyboardArrowDownIcon sx={{ color: "#2196F3" }} />
            ) : priority?.toLowerCase() === "lowest" ? (
              <KeyboardDoubleArrowDownIcon sx={{ color: "#2196F3" }} />
            ) : priority?.toLowerCase() === "highest" ? (
              <KeyboardDoubleArrowUpIcon sx={{ color: "#D32F2F" }} />
            ) : priority?.toLowerCase() === "medium" ? (
              <DragHandleOutlinedIcon sx={{ color: "#ED6C02" }} />
            ) : null}
          </>
          {websites?.map((website: string, index: number) => (
            <Chip
              key={index}
              label={website}
              color="secondary"
              sx={{
                height: "26px",
              }}
            />
          ))}
          {bizLayerQuery && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<CodeIcon />}
              onClick={() => setIsQuerySourceOpen(true)}
              sx={{ ml: "auto" }}
            >
              Query Source
            </Button>
          )}
        </Stack>
        <Dialog
          open={isQuerySourceOpen}
          onClose={() => {}}
          disableEscapeKeyDown
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" component="div">
              SQL Query
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <SqlEditor
              value={bizLayerQuery || ""}
              readOnly
              showFormatButton={false}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              color="secondary"
              variant="outlined"
              onClick={() => setIsQuerySourceOpen(false)}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="secondary"
              endIcon={<ContentCopyIcon />}
              onClick={handleCopyQuery}
            >
              Copy Query
            </Button>
          </DialogActions>
        </Dialog>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            fontSize: "14px",
            mt: 0.5,
          }}
        >
          {/* Category */}
          <Typography variant="body2">
            <Typography component="span" sx={{ fontWeight: 500 }}>
              Category:
            </Typography>{" "}
            {category}
          </Typography>

          {/* Owners */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">
              <Typography component="span" sx={{ fontWeight: 500 }}>
                Owners:
              </Typography>
            </Typography>
            <AvatarGroup
              max={3}
              sx={{ "& .MuiAvatar-root": { width: 24, height: 24 } }}
            >
              {ownerNames?.map((owner: string, index: number) => (
                <Avatar
                  key={index}
                  alt={owner.toUpperCase()}
                  src="/static/images/avatar/1.jpg"
                />
              ))}
            </AvatarGroup>
          </Box>

          {/* Created on */}
          {/* <Typography variant="body2">
            <Typography component="span" sx={{ fontWeight: 500 }}>
              Created on:
            </Typography>{" "}
            21/03/23
          </Typography> */}

          {/* Last Updated */}
          <Typography variant="body2">
            <Typography component="span" sx={{ fontWeight: 500 }}>
              Last Updated:
            </Typography>{" "}
            {lastUpdatedFromTasks ? formatDate(lastUpdatedFromTasks) : " "}
          </Typography>
        </Box>
        <Typography variant="body2" mt={0.5} color="text.secondary">
          {
            tasksByTaskStructureSharedUid[0]?.data?.["meta_data:website_name"]
              ?.description
          }
        </Typography>
        <Stack direction="row">
          <Typography gutterBottom>{description}</Typography>
        </Stack>
      </Paper>
      <Paper sx={{ padding: "8px 16px", marginTop: 2 }}>
        <GroupedOccurrencesTable />
      </Paper>
    </>
  );
};

export default TaskNameDataAccuracy;
